import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import { Button } from './ui/button';

export default function UnstampCodeModal({ isOpen, onClose, onSubmit }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);

  const handleCodeInput = (index, value) => {
    if (value.length > 1) return;
    if (!/^[0-9]*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`unstamp-pin-${index + 1}`)?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`unstamp-pin-${index - 1}`)?.focus();
    }
  };

  const handleSubmit = async (fullCode) => {
    setIsLoading(true);
    const success = await onSubmit(fullCode || code.join(''));
    setIsLoading(false);
    
    if (success) {
      setCode(['', '', '', '', '', '']);
      onClose();
    } else {
      setCode(['', '', '', '', '', '']);
      document.getElementById('unstamp-pin-0')?.focus();
    }
  };

  const handleClose = () => {
    setCode(['', '', '', '', '', '']);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            data-testid="unstamp-code-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div 
              className="backdrop-blur-xl rounded-3xl p-8 pointer-events-auto relative overflow-hidden max-w-md w-full border-2"
              style={{
                background: 'rgba(10, 0, 30, 0.9)',
                borderColor: '#FF00FF',
                boxShadow: '0 0 40px rgba(255, 0, 255, 0.5)'
              }}
              data-testid="unstamp-code-modal"
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
                data-testid="close-unstamp-modal"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Content */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Lock className="w-8 h-8 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px #00FFFF)' }} />
                  <h2 className="text-2xl font-bold text-white" style={{ textShadow: '0 0 10px rgba(255, 255, 255, 0.5)' }}>
                    Code requis
                  </h2>
                </div>

                <p className="text-gray-300 mb-8">
                  Entrez votre code vendeur ou admin pour décocher cette case
                </p>

                {/* PIN Input */}
                <div className="flex gap-2 justify-center mb-6">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`unstamp-pin-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeInput(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      disabled={isLoading}
                      className="w-11 h-14 text-center text-xl font-bold rounded-xl border-2 focus:outline-none focus:ring-4 transition-all"
                      style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        borderColor: digit ? '#FF00FF' : '#00FFFF',
                        color: '#FFFFFF',
                        boxShadow: digit ? '0 0 20px rgba(255, 0, 255, 0.5)' : '0 0 20px rgba(0, 255, 255, 0.3)',
                        textShadow: '0 0 10px #FFFFFF'
                      }}
                      data-testid={`unstamp-pin-input-${index}`}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="flex-1 py-3"
                    style={{
                      background: 'rgba(100, 100, 100, 0.3)',
                      borderColor: '#666666',
                      color: '#FFFFFF'
                    }}
                    data-testid="cancel-unstamp"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isLoading || code.some(d => !d)}
                    className="flex-1 py-3 border-2"
                    style={{
                      background: code.every(d => d) ? 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)' : 'rgba(100, 100, 100, 0.3)',
                      borderColor: '#00FFFF',
                      boxShadow: code.every(d => d) ? '0 0 30px rgba(0, 255, 255, 0.5)' : 'none',
                      textShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                    }}
                    data-testid="submit-unstamp-code"
                  >
                    {isLoading ? 'Vérification...' : 'Valider'}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  Code d'urgence disponible en cas de besoin
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
